import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import { kUserStatusLoggedIn, selectUser } from 'state/userSlice';
import { selectProject, setStateFromSearchParams } from 'state/workflowSlice';

import {
  LoggedInAppHeader,
  ProjectAppHeader,
} from 'features/app_header/AppHeader';
import {
  ProjectSwitcher,
  userHasProjectAccess,
} from 'features/app_header/ProjectSwitcher';
import { ManageButton } from 'features/nav_bar/ManageButton';
import { NavBar } from 'features/nav_bar/NavBar';
import { Workspace } from 'features/workspace/Workspace';

import { useAppDispatchUserStatus } from './AppDispatcher';
import { usePageTitle } from './usePageTitle';

import './App.css';

export function AppUrlChangeListener() {
  const dispatch = useAppDispatch();

  const handlePopStateEvent = () => {
    dispatch(setStateFromSearchParams(window.location.search));
  };

  useEffect(() => {
    window.addEventListener('popstate', handlePopStateEvent);
    return () => window.removeEventListener('popstate', handlePopStateEvent);
  });
  return null;
}

export function App() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const project = useAppSelector(selectProject);
  const userStatus = useAppSelector((state) => state.user.status);
  const initialSearchParams = useAppSelector(
    (state) => state.user.initialSearchParams,
  );
  const pageTitle = usePageTitle();
  useAppDispatchUserStatus();

  const hasProjectAccess = userHasProjectAccess(user, project);

  const [didInitSearchParams, setDidInitSearchParams] = useState(false);

  useEffect(() => {
    if (userStatus === kUserStatusLoggedIn && !didInitSearchParams) {
      const shouldHydrateFromSearchParams =
        !location.state?.skipInitialSearchHydrate;
      if (shouldHydrateFromSearchParams) {
        dispatch(setStateFromSearchParams(initialSearchParams));
      }
      setDidInitSearchParams(true);
    }
  }, [
    dispatch,
    userStatus,
    initialSearchParams,
    didInitSearchParams,
    location.state,
  ]);

  if (userStatus === kUserStatusLoggedIn && didInitSearchParams) {
    if (hasProjectAccess) {
      return (
        <div className="App">
          <Helmet>
            <title>{pageTitle}</title>
          </Helmet>
          <ProjectAppHeader />
          <div className="App-body">
            <NavBar />
            <Workspace />
          </div>
        </div>
      );
    } else {
      const create_mode =
        user && user.has_sub_admin && user.projects.length === 0;
      return (
        <div className="App">
          <Helmet>
            <title>{pageTitle}</title>
          </Helmet>
          <LoggedInAppHeader />
          <div className="App-body">
            <div className="no_project_container">
              <p>
                It appears you don’t have access to the project &lsquo;{project}
                &rsquo;, if you think this may be a mistake, please contact
                support@streetlightdata.com, or
                {create_mode ? ' create' : ' select'} a project here:
              </p>
              {create_mode ? <ManageButton /> : <ProjectSwitcher />}
            </div>
          </div>
        </div>
      );
    }
  }
}
